// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, useState, useContext, SyntheticEvent, useCallback } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  mediaMax,
  spacing,
  isMediaMax,
  zIndex,
  fontSize,
  borderRadius,
} from 'presentations/views/styles';
import {
  white,
  gray01,
  gray05,
  primary,
  gray04,
  gray02,
} from 'presentations/views/styles/color';
import Image from 'presentations/views/components/atoms/Image';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import {
  dashboardPath,
  homePath,
  loginPath,
  signUpPath,
  webPath,
} from 'presentations/routes/path';
import { ViewerItem } from 'presentations/api/schema/user';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import Logo, { SymbolLogo } from 'presentations/views/components/atoms/Logo';
import { ServicePortalContext } from 'presentations/reducers/ServicePortal';
import { useRouter } from 'next/router';
import FloatSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';
import { setEvent } from 'presentations/views/helpers/analytics';
import { Amplitude } from 'presentations/views/helpers/analytics/amplitude';

interface Props {
  viewer: ViewerItem | null;
  isLoading?: boolean;
  headerTeamImage?: string | null;
  isShowPromotion?: boolean;
}

const LoadingComponent = ({ isMobile }: { isMobile: boolean }) => {
  return isMobile ? (
    <>
      <Skeleton width={32} />
      &nbsp;
      <Skeleton width={32} />
    </>
  ) : (
    <>
      <Skeleton width={32} />
      &nbsp;
      <Skeleton width={32} />
      &nbsp;
      <Skeleton width={32} />
    </>
  );
};

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const { state: serviceState } = useContext(ServicePortalContext);
  const [anchor, setAnchor] = useState<any>(null);

  const isLoading = props.isLoading || !serviceState.isInitialized;
  const isMobile = isMediaMax('tablet');
  const router = useRouter();

  const options = [
    {
      icon: 'login',
      label: t('Sign In'),
    },
    {
      icon: 'account',
      label: t('Sign Up'),
    },
    {
      icon: 'home',
      label: t('Home'),
    },
  ];

  const onTrack = useCallback((content: string, e?: SyntheticEvent<any>) => {
    if (e) e.preventDefault();
    setEvent('click event type header menu', {
      content,
    });
  }, []);

  const onTrackSignUp = (e?: SyntheticEvent<any>) => {
    setEvent('click event type header menu', {
      content: 'sign up',
    });
    Amplitude.signUpLeadClick({
      pageType: 'eventType',
      to: 'signUp',
      position: 'header',
    });
  };

  const onTrackTop = (e?: SyntheticEvent<any>) => {
    setEvent('click event type header menu', {
      content: 'top',
    });
    Amplitude.signUpLeadClick({
      pageType: 'eventType',
      to: 'top',
      position: 'header',
    });
  };

  const onSelectMenu = async (selectedIndex: number) => {
    setAnchor(null);
    switch (selectedIndex) {
      case 0:
        onTrack('sign in');
        window.location.href = loginPath({ to: webPath(router.asPath) });
        break;
      case 1:
        onTrackSignUp();
        window.location.href = signUpPath({ to: webPath(router.asPath) });
        break;
      case 2:
        onTrackTop();
        router.push(homePath());
        break;
      default:
        break;
    }
  };

  const onClickMenu = (e: SyntheticEvent<HTMLDivElement>) => {
    setAnchor(e.currentTarget);
  };

  const onCloseMenu = () => {
    setAnchor(null);
  };

  if (!props.headerTeamImage && !props.isShowPromotion) {
    return null;
  }

  return (
    <>
      <Container>
        <LeftBox>
          {props.headerTeamImage && (
            <HeaderTeamImage
              src={props.headerTeamImage}
              alt={props.viewer?.team?.name ?? translate('siteName')}
            />
          )}
        </LeftBox>
        {/* <BrandContainer>
          {isMobile && props.headerTeamImage && (
            <HeaderTeamImage
              src={props.headerTeamImage}
              alt={props.viewer?.team?.name ?? translate('siteName')}
            />
          )}
        </BrandContainer> */}
        <RightBox>
          <MenuList>
            {isLoading ? (
              <LoadingComponent isMobile={isMobile} />
            ) : props.viewer ? (
              <MenuListItem>
                <PromotionButton href={dashboardPath()}>
                  {t('Dashboard')}
                </PromotionButton>
              </MenuListItem>
            ) : (
              props.isShowPromotion && (
                <PromotionContainer>
                  <PromotionText>
                    {t(props.headerTeamImage ? 'Powered' : 'Text')}
                  </PromotionText>
                  {!isMobile ? (
                    <>
                      <PromotionLink onClick={onTrackTop} href={homePath()}>
                        <PromotionLogo />
                      </PromotionLink>
                      <PromotionButton
                        onClick={onTrackSignUp}
                        href={signUpPath({ to: webPath(router.asPath) })}
                      >
                        {t('Sign Up')}
                      </PromotionButton>
                    </>
                  ) : (
                    <PromotionMenuContainer onClick={onClickMenu}>
                      <PromotionSymbol />
                      <PromotionLinkIcon
                        name={anchor ? 'expand_less' : 'expand_more'}
                      />
                    </PromotionMenuContainer>
                  )}
                </PromotionContainer>
              )
            )}
          </MenuList>
        </RightBox>
      </Container>
      <FloatSettingMenu
        onCloseMenu={onCloseMenu}
        onSelectMenu={onSelectMenu}
        anchor={anchor}
        options={options}
      />
    </>
  );
});

const Container = styled.div`
  display: flex;
  align-items: center;
  height: 80px;
  /* position: sticky;
  top: 0; */
  background-color: ${white};
  border-bottom: 1px solid ${gray05};
  -webkit-app-region: drag;
  z-index: ${zIndex(100)};
  ${mediaMax.tablet} {
    height: 54px;
    /* z-index: ${zIndex(1400)}; */
    justify-content: space-between;
  }
`;
const LeftBox = styled.div`
  margin-right: auto;
  margin-left: ${spacing(24)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    margin-left: ${spacing(16)};
    min-width: 80px;
    margin-right: initial;
  }
  ${mediaMax.xs} {
    width: 34px;
    overflow: hidden;
  }
`;
const HeaderTeamImage = styled(Image)`
  max-height: 40px;
  max-width: 240px;
  height: auto;
  width: auto;

  ${mediaMax.tablet} {
    max-height: 32px;
    max-width: 240px;
  }
`;
const RightBox = styled.div`
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
    min-width: 80px;
  }
  ${mediaMax.xs} {
    margin-right: ${spacing(3)};
  }
`;
const MenuList = styled.ul`
  display: flex;
  align-items: center;
`;
const MenuListItem = styled.li`
  margin-right: ${spacing(20)};
  color: ${gray01};
  :last-of-type {
    margin-right: 0;
  }
`;
const BrandContainer = styled.div`
  flex: 1;
  text-align: center;
`;
const DashboardButton = styled(Button)`
  ${mediaMax.tablet} {
    width: 96px;
    padding: ${spacing(6)} ${spacing(6)};
    letter-spacing: 0;
    font-size: ${fontSize(12)};
  }
`;

const PromotionContainer = styled.div`
  display: flex;
  align-items: center;
`;
const PromotionText = styled.div`
  font-size: ${fontSize(11)};
  margin-right: ${spacing(8)};
  color: ${gray01};
`;
const PromotionLogo = styled(Logo)`
  width: 64px;
  margin-right: ${spacing(12)};
`;
const PromotionSymbol = styled(SymbolLogo)`
  width: 20px;
  margin-right: ${spacing(4)};
`;
const PromotionMenuContainer = styled.div`
  display: flex;
  align-items: center;
`;
const PromotionButton = styled.a`
  border: 1px solid ${gray04};
  color: ${gray02};
  padding: ${spacing(8)} ${spacing(12)};
  font-size: ${fontSize(12)};
  border-radius: ${borderRadius(6)};
  :hover {
    border: 1px solid ${primary};
    color: ${primary};
  }
`;
const PromotionLink = styled.a`
  display: flex;
  align-items: center;
`;
const PromotionLinkIcon = styled(Icon)`
  display: flex;
  align-items: center;
  width: 14px !important;
  height: 14px !important;
`;

export default Component;
